import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useUpdateGatewayLabelMutation } from "../api/domusApiSlice"
import { relativeTime } from "utility/TimeUtility"

const OFFLINE_THRESHOLD_SECONDS = 5 * 60

// Deterministic pseudo-random from MAC — stable across renders, unique per device
function seedFromMac(mac) {
    return mac.split("").reduce((a, c, i) => (a + c.charCodeAt(0) * (i + 1)) & 0xffff, 0)
}

const DomusGatewayCard = ({ gateway }) => {
    const { macAddress, label, temperature, humidity, gasDetected, floodDetected, lightLevel, noiseLevel, lastUpdate } = gateway
    const [t] = useTranslation(["domus"])

    const [editMode, setEditMode] = useState(false)
    const [editLabel, setEditLabel] = useState(label)
    const [updateLabel] = useUpdateGatewayLabelMutation()

    const isOnline = (Date.now() / 1000 - lastUpdate) < OFFLINE_THRESHOLD_SECONDS
    const hasAlert = gasDetected || floodDetected

    const seed = seedFromMac(macAddress)
    const mockKwh = ((seed % 75 + 5) / 10).toFixed(1)   // 0.5 – 8.0 kWh
    const mockPeople = seed % 12                           // 0 – 11

    const handleSaveLabel = async () => {
        await updateLabel({ macAddress, label: editLabel })
        setEditMode(false)
    }

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleSaveLabel()
        if (e.key === "Escape") setEditMode(false)
    }

    return (
        <div className="col">
            <div
                className={`card h-100 ${hasAlert ? "border-danger" : "bg-dark"}`}
                style={{
                    borderWidth: hasAlert ? 2 : 1,
                    background: hasAlert ? "rgba(220,53,69,0.07)" : undefined
                }}
            >
                {/* Header */}
                <div className="card-header d-flex justify-content-between align-items-center">
                    {editMode ? (
                        <div className="input-group input-group-sm">
                            <input
                                autoFocus
                                className="form-control"
                                value={editLabel}
                                onChange={e => setEditLabel(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <button className="btn btn-success btn-sm" onClick={handleSaveLabel}>
                                <i className="fa-solid fa-check" />
                            </button>
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => setEditMode(false)}>
                                <i className="fa-solid fa-xmark" />
                            </button>
                        </div>
                    ) : (
                        <>
                            <span className="fw-semibold">
                                <i className="fa-solid fa-house me-2 text-muted" />
                                {label}
                            </span>
                            <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => { setEditLabel(label); setEditMode(true) }}
                            >
                                <i className="fa-solid fa-pen" />
                            </button>
                        </>
                    )}
                </div>

                {/* Alert sub-state banner */}
                {hasAlert && (
                    <div
                        className="d-flex align-items-center gap-3 px-3 py-2"
                        style={{
                            background: "rgba(220,53,69,0.18)",
                            borderBottom: "1px solid rgba(220,53,69,0.35)"
                        }}
                    >
                        <i
                            className="fa-solid fa-triangle-exclamation text-danger fa-beat-fade"
                            style={{ fontSize: "1.3rem", "--fa-beat-fade-opacity": 0.4 }}
                        />
                        <div className="flex-grow-1">
                            <div
                                className="fw-bold text-danger"
                                style={{ fontSize: "0.72rem", letterSpacing: "0.1em" }}
                            >
                                {t("domus:alert.active")}
                            </div>
                            <div className="d-flex gap-1 mt-1">
                                {gasDetected && (
                                    <span className="badge text-bg-danger" style={{ fontSize: "0.65rem" }}>
                                        <i className="fa-solid fa-wind me-1" />
                                        {t("domus:sensor.gas")}
                                    </span>
                                )}
                                {floodDetected && (
                                    <span className="badge text-bg-danger" style={{ fontSize: "0.65rem" }}>
                                        <i className="fa-solid fa-water me-1" />
                                        {t("domus:sensor.flood")}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Body — sensor grid */}
                <div className="card-body pb-2">

                    {/* Primary metrics: temperature + humidity */}
                    <div className="row g-0 text-center mb-3">
                        <div className="col-6 border-end" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                            <div className="fs-3 fw-bold">
                                {temperature != null
                                    ? <>{temperature.toFixed(1)}<span className="fs-6 fw-normal text-muted ms-1">°C</span></>
                                    : <span className="text-muted fs-4">—</span>
                                }
                            </div>
                            <small className="text-muted d-flex align-items-center justify-content-center gap-1">
                                <i className="fa-solid fa-temperature-half" style={{ color: "#f59e0b" }} />
                                {t("domus:sensor.temperature")}
                            </small>
                        </div>
                        <div className="col-6">
                            <div className="fs-3 fw-bold">
                                {humidity != null
                                    ? <>{humidity.toFixed(0)}<span className="fs-6 fw-normal text-muted ms-1">%</span></>
                                    : <span className="text-muted fs-4">—</span>
                                }
                            </div>
                            <small className="text-muted d-flex align-items-center justify-content-center gap-1">
                                <i className="fa-solid fa-droplet text-info" />
                                {t("domus:sensor.humidity")}
                            </small>
                        </div>
                    </div>

                    {/* Secondary metrics: 2×2 grid */}
                    <div
                        className="py-2 mb-3 rounded"
                        style={{ background: "rgba(255,255,255,0.04)" }}
                    >
                        {/* Row 1: real ambient sensors */}
                        <div className="row g-0 text-center">
                            <div className="col-6 border-end" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                                <div className="fw-semibold" style={{ fontSize: "1.1rem" }}>
                                    {lightLevel != null
                                        ? <>{lightLevel.toFixed(0)}<span className="fw-normal text-muted ms-1" style={{ fontSize: "0.75rem" }}>%</span></>
                                        : <span className="text-muted">—</span>
                                    }
                                </div>
                                <small className="text-muted d-flex align-items-center justify-content-center gap-1">
                                    <i className="fa-solid fa-sun" style={{ color: "#fbbf24" }} />
                                    {t("domus:sensor.light")}
                                </small>
                            </div>
                            <div className="col-6">
                                <div className="fw-semibold" style={{ fontSize: "1.1rem" }}>
                                    {noiseLevel != null
                                        ? <>{noiseLevel.toFixed(0)}<span className="fw-normal text-muted ms-1" style={{ fontSize: "0.75rem" }}>%</span></>
                                        : <span className="text-muted">—</span>
                                    }
                                </div>
                                <small className="text-muted d-flex align-items-center justify-content-center gap-1">
                                    <i className="fa-solid fa-volume-high" style={{ color: "#94a3b8" }} />
                                    {t("domus:sensor.noise")}
                                </small>
                            </div>
                        </div>
                        <hr className="my-1 mx-3" style={{ borderColor: "rgba(255,255,255,0.08)" }} />
                        {/* Row 2: mocked metrics */}
                        <div className="row g-0 text-center">
                            <div className="col-6 border-end" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                                <div className="fw-semibold" style={{ fontSize: "1.1rem" }}>
                                    {mockKwh}
                                    <span className="fw-normal text-muted ms-1" style={{ fontSize: "0.75rem" }}>kWh</span>
                                </div>
                                <small className="text-muted d-flex align-items-center justify-content-center gap-1">
                                    <i className="fa-solid fa-bolt" style={{ color: "#a3e635" }} />
                                    {t("domus:sensor.consumption")}
                                </small>
                            </div>
                            <div className="col-6">
                                <div className="fw-semibold" style={{ fontSize: "1.1rem" }}>
                                    {mockPeople}
                                    <span className="fw-normal text-muted ms-1" style={{ fontSize: "0.75rem" }}>pax</span>
                                </div>
                                <small className="text-muted d-flex align-items-center justify-content-center gap-1">
                                    <i className="fa-solid fa-person" style={{ color: "#818cf8" }} />
                                    {t("domus:sensor.people")}
                                </small>
                            </div>
                        </div>
                    </div>

                    {/* Boolean sensor states */}
                    {(gasDetected != null || floodDetected != null) && (
                        <div className="d-flex gap-2">
                            {gasDetected != null && (
                                <span
                                    className={`badge flex-fill py-2 ${gasDetected ? "text-bg-danger" : "text-bg-success"}`}
                                    style={{ fontSize: "0.72rem" }}
                                >
                                    <i className={`fa-solid ${gasDetected ? "fa-wind fa-beat" : "fa-circle-check text-success"} me-1`} />
                                     {t("domus:sensor.gas")}  {gasDetected ? "!" : ""}
                                </span>
                            )}
                            {floodDetected != null && (
                                <span
                                    className={`badge flex-fill py-2 ${floodDetected ? "text-bg-danger" : "text-bg-success"}`}
                                    style={{ fontSize: "0.72rem" }}
                                >
                                    <i className={`fa-solid ${floodDetected ? "fa-water fa-beat " : "fa-circle-check text-success"} me-1`} />
                                      {t("domus:sensor.flood")} {floodDetected ? "!" : ""}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div
                    className="card-footer d-flex justify-content-between align-items-center"
                    style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
                >
                    <small className={isOnline ? "text-success" : "text-muted"}>
                        <i className={`fa-solid fa-circle me-1 ${isOnline ? "fa-beat" : ""}`} style={{ fontSize: "0.5rem" }} />
                        {isOnline ? t("domus:status.online") : t("domus:status.offline")}
                    </small>
                    <small className="text-muted">
                        <i className="fa-regular fa-clock me-1" />
                        {relativeTime(lastUpdate)}
                    </small>
                </div>
            </div>
        </div>
    )
}

export default DomusGatewayCard
